import React from "react";

import Svg from "@/styles/Svg";
import { isTouchDevice } from "@/utils/getters";

import { OptionEnum, OptionModeEnum, OptionTypeEnum } from "../index";
import {
  FadeIn,
  Image,
  ImageWrapper,
  Percent,
  Status,
  StatusIcon,
  StatusText,
  TextBox,
  VotedBar,
} from "./styles";

export type ImageTileProps = {
  mode: OptionModeEnum;
  type: OptionTypeEnum;
  answer?: OptionEnum;
  percent?: number;
  icons: IIcons;
  interactionId: string;
  slideId: string;
  optionId: string;
  imageUrl: string;
  description?: string;
  isClickable: boolean;
  isSecret?: boolean;
  onClick?: Function;
};

export type ImageTileState = {
  hovered: boolean;
  wasClicked: boolean;
};

export class ImageOption extends React.Component<
  ImageTileProps,
  ImageTileState
> {
  private readonly isTouchDevice: boolean;

  constructor(props: ImageTileProps) {
    super(props);
    this.handleClick = this.handleClick.bind(this);
    this.state = {
      hovered: false,
      wasClicked: false,
    };
    this.isTouchDevice = isTouchDevice();
  }

  public render(): JSX.Element {
    const { type, mode, answer, isClickable, imageUrl } = this.props;

    return (
      <ImageWrapper
        mode={mode}
        type={type}
        answer={answer}
        isClickable={isClickable}
        onClick={this.handleClick}
        onMouseOver={
          this.isTouchDevice
            ? undefined
            : () => this.setState({ hovered: true })
        }
        onMouseLeave={
          this.isTouchDevice
            ? undefined
            : () => this.setState({ hovered: false })
        }
      >
        <Image answer={answer} imageUrl={imageUrl} isClickable={isClickable}>
          {this.renderStatus()}
        </Image>
      </ImageWrapper>
    );
  }

  private renderStatus(): JSX.Element {
    const { hovered, wasClicked } = this.state;
    const {
      mode,
      type,
      icons,
      answer,
      description,
      percent,
      isClickable,
      isSecret,
    } = this.props;
    const isCorrectAnswer = answer && answer === OptionEnum.correct;
    const showAsActualSolution = !wasClicked && answer === OptionEnum.correct;
    const displayTextbox =
      (description &&
        (hovered || this.isTouchDevice) &&
        !(!isClickable || mode === OptionModeEnum.results)) ||
      (isSecret && !isClickable && answer === OptionEnum.vote);

    if (type === OptionTypeEnum.quiz) {
      const innerText = (
        <React.Fragment>
          <TextBox show={!!displayTextbox}>{description}</TextBox>
          <StatusIcon>
            <Svg
              contents={isCorrectAnswer ? icons.Success : icons.Fail}
              width={66}
              height={66}
            />
            <StatusText>
              {isCorrectAnswer ? "Richtig!" : "Leider falsch!"}
            </StatusText>
          </StatusIcon>
        </React.Fragment>
      );
      return (
        <React.Fragment>
          {answer && (
            <Status
              type={type}
              answer={answer}
              withoutRipple={showAsActualSolution}
              overlay={false}
            >
              {showAsActualSolution ? (
                <FadeIn timing={800} delay={300}>
                  {innerText}
                </FadeIn>
              ) : (
                innerText
              )}
            </Status>
          )}
        </React.Fragment>
      );
    } else {
      const hasResult = !isClickable || mode === OptionModeEnum.results;
      return (
        <React.Fragment>
          <Status
            type={type}
            answer={answer}
            overlay={!isClickable && typeof percent === "number"}
          >
            <TextBox show={!!displayTextbox}>{description}</TextBox>
            {!isSecret && hasResult && (
              <React.Fragment>
                <StatusIcon>
                  <Percent answer={answer}>{percent}%</Percent>
                  {hasResult && (
                    <StatusText lite={true}>{description}</StatusText>
                  )}
                </StatusIcon>
              </React.Fragment>
            )}
            {!isSecret && <VotedBar percent={`${percent || 0}%`} />}
          </Status>
        </React.Fragment>
      );
    }
  }

  private handleClick(): void {
    const { type, interactionId, slideId, optionId, isClickable } = this.props;

    if (!isClickable) {
      return;
    }

    if (this.props.onClick) {
      if (type === OptionTypeEnum.quiz) {
        this.props.onClick({
          quizId: interactionId,
          slideId,
          optionId,
        });
      } else {
        this.props.onClick({ interactionId, slideId, optionId });
      }
    }

    this.setState({ wasClicked: true });
  }
}
